import { useState } from 'react';
import { useGallery } from '../hooks/useGallery';
import { useGallerySelection } from '../hooks/useGallerySelection';
import { usePendingMediaPoller } from '../hooks/usePendingMediaPoller';
import { useMediaDownload } from '../hooks/useMediaDownload';
import type { DownloadableItem } from '../hooks/useMediaDownload';
import { GalleryToolbar } from './GalleryToolbar';
import { NewImageDialog } from './NewImageDialog';
import { LoadMoreSentinel } from './LoadMoreSentinel';
import { apiJson } from '../api/client';

const API_BASE = import.meta.env.VITE_API_BASE as string;
const ENDPOINT = '/api/tools/images';

interface ImageItem {
  id: string;
  title: string;
  prompt: string;
  aspect_ratio: string;
  model: string;
  koji_codename: string;
  created_at: number;
  r2_key: string | null;
  signedUrl: string | null;
  // Stage 25: async job state.
  status: 'submitted' | 'in_progress' | 'ready' | 'failed' | string;
  error_message: string | null;
  completed_at: number | null;
}

function absolute(url: string): string {
  return url.startsWith('http') ? url : `${API_BASE}${url}`;
}

// Stage 24: route thumbnails through Cloudflare Image Transformations by
// appending a `w=` modifier to the signed resource URL. The existing
// exp/sig on the signed URL authorizes the base fetch; the worker routes
// the request through cf.image when `w` is present. Lightbox / project
// ZIP keep the un-transformed URL so the full-res original is preserved.
const THUMB_WIDTH = 512;
function thumbUrl(signedUrl: string): string {
  if (!signedUrl) return '';
  const abs = absolute(signedUrl);
  return abs + (abs.includes('?') ? '&' : '?') + `w=${THUMB_WIDTH}`;
}

function modelBadge(
  model: string | undefined | null,
): { label: string; className: string } | null {
  if (!model) return null;
  if (model.startsWith('openai/') || /gpt/i.test(model)) {
    return { label: 'GPT', className: 'gallery-badge-gpt' };
  }
  if (model.includes('gemini')) return null;
  return { label: model.split('/').pop() ?? model, className: 'gallery-badge-other' };
}

function statusLabel(status: string): string {
  switch (status) {
    case 'submitted':
      return 'Queued';
    case 'in_progress':
      return 'Generating…';
    case 'ready':
      return 'Ready';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

export function ImageGallery() {
  const { items, loading, error, deleteItem, refresh, loadMore, hasMore } =
    useGallery<ImageItem>(ENDPOINT);
  const selection = useGallerySelection(ENDPOINT);
  const download = useMediaDownload({
    zipEndpoint: '/api/tools/images/zip',
    kind: 'image',
  });
  const [active, setActive] = useState<ImageItem | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);

  usePendingMediaPoller('/api/tools/images/pending', refresh);

  async function retryImage(id: string): Promise<void> {
    setRetryError(null);
    setRetryingId(id);
    try {
      await apiJson<{ retried: string; handler: string }>(
        'POST',
        `/api/tools/images/${encodeURIComponent(id)}/retry`,
      );
      // Refresh now so the card flips to "Queued" immediately. The pending
      // poller will pick up subsequent state transitions automatically.
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setRetryError(`Retry failed for ${id}: ${msg}`);
    } finally {
      setRetryingId((current) => (current === id ? null : current));
    }
  }

  const allIds = items.map((i) => i.id);

  // Stage 27: map currently-selected items into the download-helper shape.
  const selectedDownloadItems: DownloadableItem[] = items
    .filter((i) => selection.selected.has(i.id))
    .map((i) => ({
      id: i.id,
      signedUrl: i.signedUrl,
      status: i.status,
      title: i.title,
      koji_codename: i.koji_codename,
      // Images ship as image/png today (generated_images.mime_type default).
      mime_type: 'image/png',
    }));

  return (
    <section className="gallery gallery-images">
      <GalleryToolbar
        onNew={() => setDialogOpen(true)}
        newLabel="+ New image"
        selection={selection}
        allIds={allIds}
        download={{
          selectedItems: selectedDownloadItems,
          downloadOne: download.downloadOne,
          downloadZip: download.downloadZip,
          busy: download.busy,
          error: download.error,
        }}
      />
      {retryError ? (
        <div className="status-missing small" role="alert">
          {retryError}
        </div>
      ) : null}
      {loading && items.length === 0 ? (
        <div className="muted">Loading images…</div>
      ) : error ? (
        <div className="status-missing">Error: {error}</div>
      ) : items.length === 0 ? (
        <p className="muted gallery-empty">No generated images yet.</p>
      ) : (
        <div className="gallery-grid">
          {items.map((img) => {
            const isSelected = selection.selected.has(img.id);
            const isReady = img.status === 'ready' && img.signedUrl;
            const isFailed = img.status === 'failed';
            const isPending =
              img.status === 'submitted' || img.status === 'in_progress';
            return (
              <div
                key={img.id}
                className={
                  'gallery-card' +
                  (isSelected ? ' gallery-card-selected' : '') +
                  (isPending ? ' gallery-card-pending' : '') +
                  (isFailed ? ' gallery-card-failed' : '')
                }
              >
                {selection.selectMode ? (
                  <label className="gallery-select-label">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => selection.toggleSelected(img.id)}
                      aria-label={`Select ${img.title || img.koji_codename}`}
                    />
                  </label>
                ) : null}

                {isReady ? (
                  <button
                    type="button"
                    className="gallery-thumb-btn"
                    onClick={() => {
                      if (selection.selectMode) {
                        selection.toggleSelected(img.id);
                      } else {
                        setActive(img);
                      }
                    }}
                  >
                    <img
                      src={thumbUrl(img.signedUrl as string)}
                      alt={img.title || img.koji_codename}
                      className="gallery-thumb"
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                ) : (
                  <div className="gallery-thumb gallery-thumb-placeholder">
                    <div className="gallery-status-badge">
                      {statusLabel(img.status)}
                    </div>
                    {isPending ? (
                      <div className="muted small" style={{ marginTop: 6 }}>
                        ~30 s
                      </div>
                    ) : null}
                  </div>
                )}

                <div className="gallery-meta">
                  <div className="gallery-title" title={img.prompt}>
                    {img.title || img.koji_codename}
                    {(() => {
                      const b = modelBadge(img.model);
                      return b ? (
                        <span className={`gallery-badge ${b.className}`}>
                          {b.label}
                        </span>
                      ) : null;
                    })()}
                  </div>
                  <div className="muted small">
                    {img.aspect_ratio} · {img.koji_codename}
                  </div>
                  {isFailed && img.error_message ? (
                    <div
                      className="status-missing small"
                      title={img.error_message}
                    >
                      {img.error_message.slice(0, 80)}
                      {img.error_message.length > 80 ? '…' : ''}
                    </div>
                  ) : null}
                </div>

                {!selection.selectMode ? (
                  <div className="gallery-card-actions">
                    {isFailed ? (
                      <button
                        className="btn-secondary small"
                        disabled={retryingId === img.id}
                        onClick={() => void retryImage(img.id)}
                        title="Re-queue this image with the original prompt"
                      >
                        {retryingId === img.id ? 'Retrying…' : '↻ Retry'}
                      </button>
                    ) : null}
                    <button
                      className="btn-danger small"
                      onClick={() => {
                        if (confirm('Delete this image?'))
                          void deleteItem(img.id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      {active && active.signedUrl ? (
        <div
          className="lightbox-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setActive(null)}
        >
          <img
            src={absolute(active.signedUrl)}
            alt={active.title}
            className="lightbox-image"
          />
        </div>
      ) : null}
      <LoadMoreSentinel
        hasMore={hasMore}
        loading={loading}
        onReach={() => void loadMore()}
      />
      <NewImageDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={() => void refresh()}
      />
    </section>
  );
}
